import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CheckIcon } from 'lucide-react';

export interface ButtonGroupItemProps {
  value: string | number;
  label: string;
  isSelected: boolean;
  onClick: (value: string | number) => void;
}

export function ButtonGroupItem({
  value,
  label,
  isSelected,
  onClick,
}: ButtonGroupItemProps) {
  return (
    <Button
      type="button"
      variant={isSelected ? 'default' : 'outline'}
      className={cn(
        'w-full justify-between px-3 py-1.5 h-auto text-sm',
        isSelected ? 'font-medium' : ''
      )}
      onClick={() => onClick(value)}
    >
      <span>{label}</span>
      <span className="w-4 h-4 inline-flex items-center justify-center">
        {isSelected && <CheckIcon className="h-3 w-3" />}
      </span>
    </Button>
  );
}

interface ButtonGroupProps<T extends string | number> {
  values: { value: T; label: string }[];
  value: T | T[];
  onChange: (value: T | T[]) => void;
  multiple?: boolean;
  className?: string;
  columns?: number | { sm?: number; md?: number; lg?: number };
  layout?: 'grid' | 'flex';
}

export function ButtonGroup<T extends string | number>({
  values,
  value,
  onChange,
  multiple = false,
  className,
  columns = { sm: 2, md: 4 },
  layout = 'grid',
}: ButtonGroupProps<T>) {
  const handleClick = (clickedValue: T) => {
    if (multiple) {
      const valueArray = Array.isArray(value) ? value : [value as T];
      const newValues = valueArray.includes(clickedValue)
        ? valueArray.filter((v) => v !== clickedValue)
        : [...valueArray, clickedValue];

      onChange(newValues as T | T[]);
    } else {
      onChange(clickedValue as T | T[]);
    }
  };

  const isValueSelected = (itemValue: T) => {
    if (Array.isArray(value)) {
      return value.includes(itemValue);
    }
    return value === itemValue;
  };

  // Handle columns configuration
  const getColumnsClass = () => {
    if (typeof columns === 'number') {
      return `grid-cols-1 sm:grid-cols-${columns}`;
    }

    const { sm = 2, md = 4, lg } = columns;
    let classNames = 'grid-cols-1';

    if (sm) classNames += ` sm:grid-cols-${sm}`;
    if (md) classNames += ` md:grid-cols-${md}`;
    if (lg) classNames += ` lg:grid-cols-${lg}`;

    return classNames;
  };

  const containerClassName =
    layout === 'grid'
      ? `grid gap-2 ${getColumnsClass()}`
      : 'flex flex-wrap gap-2';

  const buttonClassName = layout === 'flex' ? 'flex-1' : 'w-full';

  return (
    <div className={cn(containerClassName, className)}>
      {values.map((item) => (
        <div key={item.value} className={buttonClassName}>
          <ButtonGroupItem
            value={item.value}
            label={item.label}
            isSelected={isValueSelected(item.value)}
            onClick={() => handleClick(item.value)}
          />
        </div>
      ))}
    </div>
  );
}
